"use client";

import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  useAccount,
  useBalance,
  useDisconnect,
  useNetwork,
  useSwitchNetwork,
} from "wagmi";

import { freeGasApi } from "@/apis";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { handleErrorGasFree } from "@/lib/contractHelper";
import { compactAddress, formatCurrency, numberToHex } from "@/lib/function";
import { authActions } from "@/store/actions";
import { authSelector } from "@/store/auth/auth.slice";

import { IconCopy } from "../icons";
import { ButtonCopy } from "../share/button";
import { If } from "./condition";

declare global {
  interface Window {
    ethereum?: any;
  }
}
export const PopoverInfoWallet = () => {
  const dispatch = useDispatch();
  const {
    isRightAddress,
    isConnectWallet,
    isVerifyWallet,
    balanceGas,
    balanceLimit,
  } = useSelector(authSelector);
  const { connector, address } = useAccount();
  const { disconnect } = useDisconnect();
  const { chain } = useNetwork() as any;

  const [connectorId, setConnectorId] = useState<string | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [loadingGetFreeGas, setLoadingGetFreeGas] = useState(false);
  const [curentChains, setCurrentChains] = useState(null);

  const {
    chains,
    switchNetwork,
    isLoading: isLoadingSwitch,
  } = useSwitchNetwork({
    throwForSwitchChainNotSupported: true,
    onSuccess() {
      setCurrentChains(null);
    },
    onError(error: any) {
      (async () => {
        if (error.cause?.code === -32603) {
          try {
            await window?.ethereum?.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: numberToHex(curentChains?.id),
                  rpcUrls: curentChains?.rpcUrls?.default?.http,
                  chainName: curentChains?.name,
                  nativeCurrency: curentChains?.nativeCurrency,
                  iconUrls: [curentChains?.icon],
                },
              ],
            });
          } catch (e) {
            console.log(e);
          }
        }
      })();
    },
  }) as any;

  const { refetch, data: balance } = useBalance({
    address,
    onSuccess(data) {
      dispatch(authActions.self.loadBalanceGas(data));
    },
  });

  const handleGetFreeGas = async () => {
    try {
      setLoadingGetFreeGas(true);

      await freeGasApi.getFreeGas({
        receiverAddr: address,
        chainId: numberToHex(chain.id),
      });

      await refetch();
      toast.success("Bạn đã nhận DAP Credits thành công");
    } catch (err) {
      const message = await handleErrorGasFree({
        key: err?.cause?.reason,
      });
      toast.error(message);
    } finally {
      setLoadingGetFreeGas(false);
    }
  };

  const handleOpenProfile = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).id === "gas") {
      handleGetFreeGas();
    }
    // else {
    //   onCancelMenu();
    //   setTimeout(() => {
    //     dispatch(alertActions.setShowProfileModal(true));
    //   }, 500);
    // }
  };

  useEffect(() => {
    if (connector?.id) {
      setConnectorId(connector.id);
    }
  }, [connector]);

  return (
    <If
      condition={isLoadingSwitch}
      Then={
        <>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </>
      }
      Else={
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <If
                condition={!!connectorId}
                Then={
                  <img
                    className="w-[28px] h-[28px]"
                    alt=""
                    src={
                      connectorId
                        ? `/icons/wallets/${connectorId}.svg`
                        : "/icons/header/icon_none.svg"
                    }
                  />
                }
              />
              <span className={`flex text-sm`}>
                <span className="text-gray_700">
                  <If
                    condition={isRightAddress}
                    Then={<span>{compactAddress(address, 7)}</span>}
                    Else={
                      <span className="text-error text-[12px] underline">
                        Địa chỉ ví không hợp lệ
                      </span>
                    }
                  />
                </span>
              </span>
            </div>
            <div>
              <Popover open={isPopoverOpen}>
                <PopoverTrigger asChild>
                  <div
                    onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <img
                      className="w-[28px] h-[28px] object-cover rounded-full border border-gray_200"
                      alt=""
                      src={chain?.icon || "/icons/icon_none.svg"}
                    />
                    <img className="" src="/icons/arrow_down.svg" alt="" />
                  </div>
                </PopoverTrigger>
                <PopoverContent align="end">
                  <div className="min-w-[207px] ">
                    <h1 className="text-sm font-medium text-gray_500 mb-3">
                      Thay đổi mạng
                    </h1>
                    {chains?.map((item) => (
                      <div
                        key={item?.id}
                        className={`flex items-center justify-between px-3 py-2 hover:bg-[#F9FAFB] hover:shadow-shadowChangeNetworks cursor-pointer rounded-xl transition-all ease-linear duration-200 ${
                          chain?.id == item?.id ? "bg-gray_100" : ""
                        }`}
                        onClick={async () => {
                          setCurrentChains(item);
                          setIsPopoverOpen(false);
                          // onCancelMenu();
                          switchNetwork?.(item?.id);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <img
                            className="w-7 h-7 rounded-full"
                            src={item?.icon || "/icons/header/icon_none.svg"}
                            alt=""
                          />
                          <span className="text-sm font-bold text-gray_900">
                            {item?.name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full justify-center"
            onClick={handleOpenProfile}
            disabled={loadingGetFreeGas}
          >
            <div className="flex items-center gap-2 text-purple-700">
              <If
                condition={
                  isConnectWallet &&
                  isVerifyWallet &&
                  !(Number(balanceGas?.formatted) >= balanceLimit)
                }
                Then={
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <img
                          id="gas"
                          className={`w-5 ${
                            loadingGetFreeGas ? "animate-spin" : ""
                          }`}
                          src="/icons/icon_plus.svg"
                          alt=""
                        />
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <span className="text-xs text-gray-600">
                          <strong className="text-primary_500">
                            Nhận DAP Credits
                          </strong>{" "}
                          miễn phí
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                }
              />
              <span className="text-lg font-bold">
                {formatCurrency(balance?.formatted, 2)} {balance?.symbol}
              </span>
            </div>
          </Button>
          <div>
            <div className="flex justify-between items-center gap-2 mt-3">
              <ButtonCopy text={address} className="flex items-center gap-1">
                <IconCopy color="#FF8100" />
                <span className="text-xs font-semibold text-primary_500">
                  Sao chép
                </span>
              </ButtonCopy>

              <Button
                variant="ghost"
                onClick={() => {
                  disconnect();
                }}
                className="flex items-center gap-1 px-2 h-auto"
              >
                <img src="/icons/icon_disconect.svg" alt="disconnect" />
                <span className="text-xs font-semibold text-primary_500">
                  Ngắt kết nối
                </span>
              </Button>
            </div>
          </div>
        </>
      }
    />
  );
};
